<script setup lang="ts">
import {
  sidebarMenu,
  storeSelection,
  type MenuItemType,
  type StoreSelectionItemType,
} from './sidebar'
import DbgHealthLogo from '@/components/icons/DbgHealthLogo.vue'
import MenuItem from './MenuItem.vue'
// import Avatar from '../Avatar.vue'
import Settings from './Settings.vue'
import StoreSelection from './StoreSelection.vue'

import { onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useThemeStore, useValueDataStore } from '@/stores'
import { EntityType, type Group, type Store, type GroupSummary } from '@/data'
import { BackArrow } from '@/components/icons'
import {RoleService} from "@/services/RoleService";
import { useRouter, useRoute } from 'vue-router'
import HasRole from "@/components/common/HasRole.vue";
import SpinnerOverlay from "@/components/common/SpinnerOverlay.vue";
import {getDatasetId} from "@/dataset.ts";

const themeStore = useThemeStore()
const router = useRouter()
const route = useRoute()
const { themeColors } = themeStore
const {
  valueData,
  groupSummaryList,
  isValueDataStoreLoading,
  uiRoles,
  filteredGroupId,
  selectionId,
} =
  storeToRefs(useValueDataStore())
const { loadValueData, handleFilterData, handleClearFilter } = useValueDataStore()

const menuItems = ref<MenuItemType[]>(sidebarMenu)
const storeSelectionItems = ref<StoreSelectionItemType[]>(storeSelection)
const showStoreSelection = ref(false)
const roleService = new RoleService();

function handleStoreChange() {
  showStoreSelection.value = !showStoreSelection.value
  if (showStoreSelection.value) {
    router.push({ name: 'storesList' })
  } else {
    // Navigate back to the main menu
    const benefitsPath =
      roleService.hasRole('axe') &&
      !roleService.hasRole('vidacorp') &&
      !roleService.hasRole('arrotex')
        ? '/benefits/axe'
        : '/benefits'
    router.push(benefitsPath)
  }
}

const handleLoadData = (type: 'group' | 'store', id: string) => {
  if (type === 'group') {
    loadValueData(EntityType.GROUP, id)
  } else if (type === 'store') {
    loadValueData(EntityType.STORE, id)
  }
}

const updateStoreSelectionItems = () => {
  if (!isValueDataStoreLoading.value) {
    storeSelectionItems.value = storeSelectionItems.value.map((item) => {
      if (item.label === 'Head Office') {
        return {
          ...item,
          children: groupSummaryList.value.map((group: GroupSummary) => ({
            groupId: group.groupId,
            name: group.groupName
          }))
        }
      }
      // else if (item.label === 'Pharmacy') {
      //   return {
      //     ...item,
      //     children: storeList.value
      //       .map((store: Store) => ({
      //         storeId: store.storeId,
      //         groupId: store.groupId,
      //         name: store.name
      //       }))
      //       .filter(
      //         (item) => filteredGroupId.value === '' || item.groupId === filteredGroupId.value
      //       )
      //   }
      // }
      return item
    })
  }
}

watch(
  [isValueDataStoreLoading, groupSummaryList],
  () => {
    updateStoreSelectionItems()
  },
  { immediate: true }
)

watch(
  [uiRoles],
  () => {
    menuItems.value = sidebarMenu.filter(item => !item.role || roleService.hasRoleForMenuItem(item)) || [];
  },
  { immediate: true }
)

onMounted(() => {
  if (route.path === '/stores-list') {
    showStoreSelection.value = true
  }
  updateStoreSelectionItems()
})
</script>

<template>
  <div class="sidebar-outer">
    <div class="sidebar-top">
      <DbgHealthLogo height="3.125rem" width="6.25rem" :fill="themeColors.colorText" />
      <div class="customer-welcome">
        <!-- <Avatar /> -->
        <div class="message">
          <span>Welcome back</span>
          <span class="customer-name">{{ valueData.customerName }}</span>
          <HasRole role="administrator">
            <div class="edit-button" v-if="valueData.customerType == 'HO'">
              <router-link :to="`/admin/groups/${selectionId}/edit`" class="button-link">Edit</router-link>
            </div>
          </HasRole>
          <div class="change-store" v-if="!showStoreSelection" @click="handleStoreChange">
            <span>Click here to change HO/Pharmacy</span>
          </div>
          <div class="back" v-if="showStoreSelection" @click="handleStoreChange">
            <BackArrow />
            <span>Go back to menu</span>
          </div>
          <div>&nbsp;</div>
          <div class="dataset" v-if="getDatasetId()">
            <span>Dataset override: {{getDatasetId()}}</span>
          </div>
        </div>
      </div>
      <div class="section-breaker"></div>
    </div>
    <div class="sidebar-middle">
      <SpinnerOverlay v-if="!uiRoles?.length" />
      <div v-else class="menu-items" :class="{ hide: showStoreSelection }">
        <MenuItem
          v-for="(item, index) in menuItems"
          :key="index"
          :label="item.label"
          :path="item.path"
          :icon="item.icon"
          :depth="0"
          :data="item.children"
        ></MenuItem>
      </div>
      <div class="store-selection" :class="{ show: showStoreSelection }">
        <StoreSelection
          v-for="(item, index) in storeSelectionItems"
          :key="index"
          :label="item.label"
          :icon="item.icon"
          :data="item.children"
          :placeholder="item.placeholder"
          :selectedCustomer="valueData.customerName"
          :enableFilter="item.label === 'Head Office' && filteredGroupId !== ''"
          @loadData="handleLoadData"
          @filterData="handleFilterData"
          @clearFilter="handleClearFilter"
        ></StoreSelection>
      </div>
    </div>
    <div class="sidebar-bottom">
      <Settings />
    </div>
  </div>
</template>

<style scoped>
.sidebar-outer {
  padding: 1.875rem 1.25rem 0.625rem 1.875rem;
  background-color: var(--color-background-bright);
  width: 15%;
  min-width: 17.5rem;
  height: 100vh;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  position: relative;
  transition: background-color 0.5s ease, border-color 0.5s ease;
  border-right: 0.0625rem solid var(--color-background);
}

.sidebar-top .customer-welcome {
  display: flex;
  gap: 0.3125rem;
  align-items: start;
  margin-top: 3.125rem;
  margin-bottom: 1.875rem;
  min-height: 4.625rem;
}

.sidebar-top .customer-welcome .message {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.sidebar-top .customer-welcome .message .customer-name {
  font-size: var(--font-size-h1);
  line-height: var(--font-size-h1);
  font-weight: 700;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-top .customer-welcome .message span:first-child {
  font-size: var(--font-size-body);
  line-height: var(--font-size-body);
}

.sidebar-top .customer-welcome .message .edit-button {
  font-size: var(--font-size-body);
  line-height: var(--font-size-body);
}

.sidebar-top .customer-welcome .message .back,
.sidebar-top .customer-welcome .message .change-store {
  font-size: var(--font-size-caption);
  font-style: italic;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.3125rem;
}

.sidebar-top .customer-welcome .message .back span,
.sidebar-top .customer-welcome .message .change-store span {
  font-size: var(--font-size-caption);
  font-style: italic;
  cursor: pointer;
  font-weight: 400;
}

.sidebar-top .section-breaker {
  width: 100%;
  height: 0.3125rem;
  transition: background-color 0.5s ease;
  background-color: var(--color-background);
  position: absolute;
  left: 0;
}

.sidebar-middle {
  flex: 1;
  margin-top: 1.5625rem;
  width: 200%;
  display: flex;
  > ul {
    list-style-type: none;

    > li {
      padding: 0.3125rem;
      border-radius: 0.25rem;
    }

    > li:hover {
      cursor: pointer;
    }
  }
}

.sidebar-middle .menu-items {
  transition:
    transform 0.25s ease-in-out,
    opacity 0.25s ease 0.25s;
  width: 50%;
  transform: translateX(0%);
  opacity: 1;
}

.sidebar-middle .menu-items .menu-item {
  margin-bottom: 0.9375rem;
}

.sidebar-middle .menu-items.hide {
  transform: translateX(-120%);
  opacity: 0;
}

.sidebar-middle .store-selection {
  width: 50%;
  padding-right: 0.625rem;
  transform: translateX(100%);
  transition:
    transform 0.25s ease-in-out,
    opacity 0.25s ease 0.25s,
    max-height 0s ease 0.25s;
  opacity: 0;
  max-height: 0;
  overflow: hidden;
}

.sidebar-middle .store-selection.show {
  transform: translateX(-100%);
  opacity: 1;
  max-height: 43.75rem;
}

.sidebar-bottom {
  display: flex;
  justify-content: space-around;
  align-items: center;
  /* margin-inline: auto; */
  width: 100%;
}
</style>
